import React, { useCallback, useEffect, useState } from 'react';
import { cancelBillingActivity, listBillingActivities } from '../../api/billing';

const money = (value) => value === null || value === undefined || value === ''
  ? '—'
  : Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const BillingActivitiesTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingKey, setEditingKey] = useState(null);
  const [reasons, setReasons] = useState({});
  const [busyKey, setBusyKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await listBillingActivities();
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Impossible de charger le journal des activités.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const confirmCancel = async (row) => {
    const key = `${row.activity_type}:${row.id}`;
    const reason = (reasons[key] || '').trim();
    if (reason.length < 3) {
      setError('Le motif est obligatoire (3 caractères minimum).');
      return;
    }
    if (!window.confirm(`Confirmer l'annulation de « ${row.title} » pour ${row.company_name} ?`)) return;
    setBusyKey(key);
    setError('');
    try {
      await cancelBillingActivity(row.activity_type, row.id, reason);
      setEditingKey(null);
      setReasons((prev) => ({ ...prev, [key]: '' }));
      await load();
    } catch (err) {
      let detail = '';
      try { detail = JSON.parse(err.message).detail || ''; } catch (parseErr) { /* réponse non JSON */ }
      setError(detail || "L'annulation a échoué.");
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className="sb-card">
      <div className="sb-card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <span className="sb-card-title">Annuler / Supprimer — Journal des activités</span>
        <button type="button" className="sb-block-btn" onClick={load} disabled={loading}>Actualiser</button>
      </div>
      <div className="sb-card-body">
        <p className="sb-hint">
          Les activités annulées restent dans l'historique pour audit. Elles sont exclues des calculs concernés. Aucune suppression physique n'est effectuée.
        </p>
        {error && <p className="sb-error">{error}</p>}
        {loading ? <p>Chargement...</p> : (
          <div className="sb-table-wrap">
            <table className="sb-table">
              <thead>
                <tr>
                  <th>Date</th><th>Company</th><th>Activité</th><th>Montant USD équiv. / déclaré</th>
                  <th>Enregistré par</th><th>État</th><th>Annulation / audit</th><th>Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && <tr><td colSpan={8} className="sb-empty">Aucune activité trouvée.</td></tr>}
                {rows.map((row) => {
                  const key = `${row.activity_type}:${row.id}`;
                  const editing = editingKey === key;
                  return (
                    <React.Fragment key={key}>
                      <tr>
                        <td>{fmtDateTime(row.created_at)}</td>
                        <td>{row.company_name}</td>
                        <td>{row.title}</td>
                        <td className="sb-money">{money(row.amount)}</td>
                        <td>{row.actor || '—'}</td>
                        <td className={row.is_void ? 'sb-balance-due' : 'sb-balance-clear'}>{row.is_void ? 'Annulée' : 'Active'}</td>
                        <td>
                          {row.is_void
                            ? <span>{row.void_reason || '—'}<br />{row.voided_by || '—'} · {fmtDateTime(row.voided_at)}</span>
                            : row.can_cancel ? '—' : <span title={row.blocked_reason}>{row.blocked_reason || 'Non disponible'}</span>}
                        </td>
                        <td>
                          {row.is_void ? '—' : row.can_cancel ? (
                            <button type="button" className="sb-block-btn" onClick={() => { setError(''); setEditingKey(editing ? null : key); }} disabled={busyKey === key}>
                              {editing ? 'Fermer' : 'Annuler / Supprimer'}
                            </button>
                          ) : <button type="button" className="sb-block-btn" disabled title={row.blocked_reason}>Bloqué</button>}
                        </td>
                      </tr>
                      {editing && (
                        <tr>
                          <td colSpan={8}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                              <input
                                className="sb-input"
                                style={{ minWidth: 240, flex: 1 }}
                                maxLength={255}
                                placeholder="Motif obligatoire de l'annulation"
                                value={reasons[key] || ''}
                                onChange={(event) => setReasons((prev) => ({ ...prev, [key]: event.target.value }))}
                              />
                              <button type="button" className="sb-save-btn" disabled={busyKey === key} onClick={() => confirmCancel(row)}>
                                {busyKey === key ? '...' : "Confirmer l'annulation"}
                              </button>
                              <button type="button" className="sb-unblock-btn" disabled={busyKey === key} onClick={() => setEditingKey(null)}>Retour</button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BillingActivitiesTab;
